import { PageTitleRow } from "../../components/layout/PageTitleRow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardList, RefreshCw, Search } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, DataTable, EmptyState, LoadingState, ui } from "../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { TablePagination } from "../leave-management/components/TablePagination";
import { cn } from "../../lib/utils";
import {
  fetchOnboardingRequests,
  fetchOnboardingRequestsByStatus,
  fetchOnboardingRequestsByWorkLocation,
  searchOnboardingRequestsByName,
  updateOnboardingRequestStatus,
} from "../../services/onboardingRequestService";
import {
  isReviewableRequest,
  requestName,
  requestStatusOf,
  verificationCodeOf,
} from "./onboardingRequestFields";
import { RequestStatusBadge, formatRequestDate, formatRequestDay } from "./RequestStatusBadge";
import { RequestActionButton } from "./RequestActionButton";

const PAGE_SIZE = 10;
const ALL = "all";


export function OnboardingRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * The outcome of a decision made on the detail screen.
   *
   * Read once, at mount, from the state that screen navigated with: the reader
   * arrives here without having seen a confirmation, and the row alone does not
   * say whether their own click is what changed it.
   */
  const [flash, setFlash] = useState(location.state?.flash ?? "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  /**
   * The statuses on offer, learnt from an unfiltered read.
   *
   * Kept apart from the rows on screen: once a status is chosen the list holds
   * only that status, and rebuilding the options from it would collapse the
   * filter to the one option it could still see.
   */
  const [statuses, setStatuses] = useState([]);
  const [status, setStatus] = useState(ALL);
  const [workLocation, setWorkLocation] = useState(ALL);
  const [name, setName] = useState("");
  const [submittedName, setSubmittedName] = useState("");
  const [page, setPage] = useState(1);

  /** Which row is being rejected from the list, and what went wrong. */
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectError, setRejectError] = useState("");

  /**
   * Drop the message from history as soon as it has been read into state, so
   * reloading the list does not announce a decision made some time ago.
   */
  useEffect(() => {
    if (!location.state?.flash) return;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");

    const read = submittedName.trim()
      ? searchOnboardingRequestsByName(submittedName.trim())
      : status !== ALL
        ? fetchOnboardingRequestsByStatus(status)
        : workLocation !== ALL
          ? fetchOnboardingRequestsByWorkLocation(workLocation)
          : fetchOnboardingRequests();

    read
      .then((result) => {
        setRows(result.rows);
        if (!submittedName.trim() && status === ALL && workLocation === ALL) {
          setStatuses([
            ...new Set(
              result.rows.map((row) => String(requestStatusOf(row)).trim()).filter(Boolean)
            ),
          ]);
        }
      })
      .catch((error) => {
        console.error("Failed to load onboarding requests:", error);
        setRows([]);
        setLoadError(error.message || t("onboardingRequests.errorLoad"));
      })
      .finally(() => setLoading(false));
  }, [status, workLocation, submittedName, t]);

  useEffect(() => load(), [load]);

  /** Work locations are whatever the loaded requests name. */
  const workLocations = useMemo(
    () => [
      ...new Set(
        rows.map((row) => String(row?.workLocation ?? "").trim()).filter(Boolean)
      ),
    ],
    [rows]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  /** One filter at a time, because one endpoint answers at a time. */
  const applyStatus = (value) => {
    setStatus(value);
    setWorkLocation(ALL);
    setName("");
    setSubmittedName("");
    setPage(1);
  };

  const applyWorkLocation = (value) => {
    setWorkLocation(value);
    setStatus(ALL);
    setName("");
    setSubmittedName("");
    setPage(1);
  };

  const applyName = (event) => {
    event.preventDefault();
    setStatus(ALL);
    setWorkLocation(ALL);
    setSubmittedName(name);
    setPage(1);
  };

  /**
   * Only a request with submitted details opens. Until then the row IS the
   * whole of it — the reviewer reads the verification code from here.
   */
  const openRequest = (request) => {
    if (!isReviewableRequest(request)) return;
    navigate(`/hrms-team/employees/requests/${request.requestId ?? request.id}`);
  };

  /**
   * Reject a request that has no detail to open.
   *
   * A request still waiting on its candidate cannot be reviewed, so without
   * this there would be no way to close one that was raised by mistake. It
   * sends the status alone — the same call the detail screen's Reject makes.
   */
  const rejectRequest = async (request) => {
    const id = request.requestId ?? request.id;
    setRejectingId(id);
    setRejectError("");
    setFlash("");
    try {
      await updateOnboardingRequestStatus(id, "REJECTED");
      load();
    } catch (error) {
      console.error("Failed to reject the request:", error);
      setRejectError(error.message || t("onboardingRequests.errorDecision"));
    } finally {
      setRejectingId(null);
    }
  };

  const columns = [
    { key: "name", label: t("onboardingRequests.candidate") },
    { key: "email", label: t("common.email") },
    { key: "verificationCode", label: t("onboardingRequests.verificationCode") },
    { key: "workLocation", label: t("onboarding.labels.workLocation") },
    { key: "submittedOn", label: t("onboardingRequests.submittedOn") },
    { key: "status", label: t("onboardingRequests.status") },
    { key: "action", label: t("common.action"), className: "text-right" },
  ];

  return (
    <AppLayout>

      <div className="mb-5">
        <PageTitleRow back={<PageBackLink to="/" />}>
          <h1 className={ui.pageTitle}>{t("onboardingRequests.title")}</h1>
        </PageTitleRow>
      </div>

      {loadError ? (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 transition hover:bg-rose-100 sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("onboardingRequests.retry")}
          </button>
        </div>
      ) : null}

      {/* Filters — one at a time, and the name search is submitted rather than
          typed-through, since each keystroke would be a request. */}
      <div className="mb-4 grid grid-cols-1 gap-2.5 sm:flex sm:flex-wrap sm:items-center">
        <form onSubmit={applyName} className="relative w-full sm:w-[260px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
          <input data-page-search="true"
            type="search"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("onboardingRequests.searchPlaceholder")}
            className={cn(ui.input, "h-9 w-full pl-9")}
          />
        </form>

        <div className="w-full sm:w-[190px]">
          <Select value={status} onValueChange={applyStatus}>
            <SelectTrigger className="h-9 w-full rounded-lg border border-(--color-text-border) bg-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("onboardingRequests.allStatuses")}</SelectItem>
              {statuses.map((value) => (
                <SelectItem key={value} value={value}>
                  {value.replace(/[_-]+/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {workLocations.length > 0 ? (
          <div className="w-full sm:w-[190px]">
            <Select value={workLocation} onValueChange={applyWorkLocation}>
              <SelectTrigger className="h-9 w-full rounded-lg border border-(--color-text-border) bg-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                <SelectItem value={ALL}>{t("onboardingRequests.allLocations")}</SelectItem>
                {workLocations.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {flash ? (
        <p className="mb-3 text-[12px] font-semibold text-emerald-700">{flash}</p>
      ) : null}

      {rejectError ? (
        <p className="mb-3 text-[12px] font-semibold text-rose-600">{rejectError}</p>
      ) : null}

      <ContentCard>
        {loading ? (
          <LoadingState message={t("onboardingRequests.loading")} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={t("onboardingRequests.emptyTitle")}
            message={t("onboardingRequests.emptyMessage")}
          />
        ) : (
          <>
            <DataTable columns={columns} minWidth="1000px">
              {pagedRows.map((request) => (
                <tr
                  key={request.requestId ?? request.id}
                  onClick={() => openRequest(request)}
                  className={cn(
                    "transition",
                    isReviewableRequest(request)
                      ? "cursor-pointer hover:bg-workspace/20"
                      : "cursor-default"
                  )}
                >
                  <td className="px-4 py-3 font-semibold text-foreground">
                    {requestName(request) || "—"}
                  </td>
                  <td className="px-4 py-3 per-file-hint">
                    {request.email || "—"}
                  </td>
                  {/* What the candidate is told to quote. The reviewer reads it
                      from this row — there is nothing to open for it. */}
                  <td className="px-4 py-3 font-mono text-[12px] text-foreground">
                    {verificationCodeOf(request) || "—"}
                  </td>
                  <td className="px-4 py-3 per-file-hint">
                    {request.workLocation || "—"}
                  </td>
                  <td className="px-4 py-3 text-[11px] per-file-hint">
                    {formatRequestDate(request.createdAt, t) || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <RequestStatusBadge status={requestStatusOf(request)} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RequestActionButton
                      request={request}
                      rejectingId={rejectingId}
                      onOpen={openRequest}
                      onReject={rejectRequest}
                    />
                  </td>
                </tr>
              ))}
            </DataTable>

            {/* Mobile cards — the badge and the one action share the right
                edge, so the card stays a single compact block. */}
            <div className="divide-y divide-(--color-text-border)/40 md:hidden">
              {pagedRows.map((request) => (
                <div
                  key={request.requestId ?? request.id}
                  onClick={() => openRequest(request)}
                  className={cn(
                    "flex items-start justify-between gap-3 p-4 transition",
                    isReviewableRequest(request)
                      ? "cursor-pointer hover:bg-workspace/20"
                      : "cursor-default"
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-foreground">
                      {requestName(request) || "—"}
                    </p>
                    <p className="mt-0.5 truncate text-[12px] per-file-hint">
                      {request.email || "—"}
                    </p>
                    {/* The day alone: the hour it was raised decides nothing,
                        and the work location is a filter rather than a fact
                        worth a line on a narrow card. */}
                    <p className="mt-2 text-[11px] per-file-hint">
                      {formatRequestDay(request.createdAt, t) || "—"}
                    </p>
                    {verificationCodeOf(request) ? (
                      <p className="mt-1 font-mono text-[12px] text-foreground">
                        {verificationCodeOf(request)}
                      </p>
                    ) : null}
                  </div>

                  {/* The badge reads first, at the top; the action drops to the
                      foot of the card, clear of it and level with the last line
                      the reader has just read. */}
                  <div className="flex shrink-0 flex-col items-end justify-between gap-3 self-stretch">
                    <RequestStatusBadge status={requestStatusOf(request)} />
                    <RequestActionButton
                      request={request}
                      rejectingId={rejectingId}
                      onOpen={openRequest}
                      onReject={rejectRequest}
                    />
                  </div>
                </div>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={rows.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>
    </AppLayout>
  );
}

export default OnboardingRequestsPage;
